"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { ColorBlock } from "../ui";
import styles from "./MoreFeaturesSection.module.css";

// Image area is a ColorBlock (UI library) placeholder, one accent per card.
// Swap `color` for a real image/illustration later; the frame stays the same.
// Text sits below: bold title, muted description.
const CARDS = [
   {
    id: "low-latency",
    title: "Global low latency",
    description:
      "Served from the edge worldwide. Your videos start instantly, wherever your viewers are.",
    color: "blue",
    image: "https://assets.flare.link/landing/cards/globe-cdn.png",
  },
  {
    id: "ab-testing",
    title: "A/B testing",
    description:
      "Run two versions of a video and let the data pick the winner. Select your own traffic split.",
    color: "orange",
    image: "https://assets.flare.link/landing/cards/ab-test.png",
  },
    {
    id: "import-wistia",
    title: "Import from Wistia",
    description:
      "Bring your whole Wistia library over in a few clicks. Keep your videos, lose the bill.",
    color: "teal",
    image: "https://assets.flare.link/landing/cards/import-wistia.png",
  },
  {
    id: "video-editor",
    title: "Video editor",
    description:
      "Modify and optimize your video right in the browser. Nothing to install.",
    color: "purple",
    image: "https://assets.flare.link/landing/cards/video-editor.png",
  },
  {
    id: "manage-team",
    title: "Manage your team",
    description:
      "Invite teammates, share workspaces, and keep every video in one organized place.",
    color: "green",
    image: "https://assets.flare.link/landing/cards/teamBeige.png",
    zoom: 1.2,
  },
  {
    id: "better-value",
    title: "Better value",
    description:
      "Pro-grade hosting and analytics at a fraction of what the big players charge.",
    color: "lime",
    image: "https://assets.flare.link/landing/cards/value.png",
    zoom: 1.3,
    offsetX: -1,
    offsetY: -6,
  },
];

export default function MoreFeaturesSection() {
  const rowRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollState = useCallback(() => {
    const el = rowRef.current;
    if (!el) return;
    const maxScroll = el.scrollWidth - el.clientWidth;
    setCanScrollLeft(el.scrollLeft > 1);
    setCanScrollRight(el.scrollLeft < maxScroll - 1);
  }, []);

  useEffect(() => {
    const el = rowRef.current;
    if (!el) return undefined;
    updateScrollState();
    el.addEventListener("scroll", updateScrollState, { passive: true });
    window.addEventListener("resize", updateScrollState);
    return () => {
      el.removeEventListener("scroll", updateScrollState);
      window.removeEventListener("resize", updateScrollState);
    };
  }, [updateScrollState]);

  const scrollByDir = (dir) => {
    const el = rowRef.current;
    if (!el) return;
    // Advance exactly one card per click (card width + gap).
    const card = el.querySelector("li");
    let stride = el.clientWidth * 0.8;
    if (card) {
      const gap = parseFloat(getComputedStyle(el).columnGap || getComputedStyle(el).gap) || 0;
      stride = card.getBoundingClientRect().width + gap;
    }
    el.scrollBy({ left: dir * stride, behavior: "smooth" });
  };

  return (
    <section className={styles.section}>
      <div className={styles.container}>
        <div className={styles.header}>
          <h2 className={styles.title}>Full stack video service</h2>

          <div className={styles.controls}>
            <button
              type="button"
              className={styles.navButton}
              onClick={() => scrollByDir(-1)}
              disabled={!canScrollLeft}
              aria-label="Scroll features left"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="15 18 9 12 15 6" />
              </svg>
            </button>
            <button
              type="button"
              className={styles.navButton}
              onClick={() => scrollByDir(1)}
              disabled={!canScrollRight}
              aria-label="Scroll features right"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="9 18 15 12 9 6" />
              </svg>
            </button>
          </div>
        </div>

        <ul className={styles.cardsRow} ref={rowRef}>
          {CARDS.map((card) => (
            <li key={card.id} className={styles.card}>
              <ColorBlock
                color={card.image ? null : card.color}
                bordered={!card.image}
                className={styles.cardBlock}
                aria-hidden="true"
              >
                {card.image ? (
                  <img
                    src={card.image}
                    alt=""
                    className={styles.cardImage}
                    style={
                      card.zoom
                        ? { transform: `translate(${card.offsetX ?? 0}%, ${card.offsetY ?? 0}%) scale(${card.zoom})` }
                        : undefined
                    }
                    loading="lazy"
                    decoding="async"
                  />
                ) : null}
              </ColorBlock>
              <div className={styles.cardBody}>
                <span className={styles.cardTitle}>{card.title}</span>
                <span className={styles.cardDescription}>{card.description}</span>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
